import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {useSignIn} from '@clerk/react'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
})

export function ForgotPassword() {
  const navigate = useNavigate()
  const {signIn, errors, fetchStatus} = useSignIn()

  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState<'email' | 'code' | 'password'>('email')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [hasErrors, setHasErrors] = useState<boolean | undefined>()

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      setLoading(true)
      setHasErrors(undefined)

      try {
        const {error} = await signIn.create({
          identifier: values.email,
        })

        if (error) throw error

        const {error: sendError} = await signIn.resetPasswordEmailCode.sendCode()

        if (sendError) throw sendError

        setStep('code')
      } catch (err) {
        console.error(err)
        setHasErrors(true)
      }

      setLoading(false)
    },
  })

  const verifyCode = async () => {
    try {
      const {error} = await signIn.resetPasswordEmailCode.verifyCode({
        code,
      })

      if (error) throw error

      if (signIn.status === 'needs_new_password') {
        setStep('password')
      }
    } catch (err) {
      console.error(err)
      setHasErrors(true)
    }
  }

  const submitNewPassword = async () => {
    try {
      const {error} = await signIn.resetPasswordEmailCode.submitPassword({
        password,
      })

      if (error) throw error

      if (signIn.status === 'complete') {
        await signIn.finalize({
          navigate: ({decorateUrl}) => {
            const url = decorateUrl('/dashboard')
            navigate(url)
          },
        })
      }
    } catch (err) {
      console.error(err)
      setHasErrors(true)
    }
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        <h1 className='text-dark fw-bolder mb-3'>Forgot Password?</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Reset your password in three simple steps.
        </div>
      </div>

      {hasErrors && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>Something went wrong. Please try again.</div>
        </div>
      )}

      {/* STEP 1 — EMAIL */}
      {step === 'email' && (
        <>
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>

            <input
              type='email'
              autoComplete='off'
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control bg-transparent',
                {'is-invalid': formik.touched.email && formik.errors.email},
                {'is-valid': formik.touched.email && !formik.errors.email}
              )}
            />

            {formik.touched.email && formik.errors.email && (
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            )}
          </div>

          <div className='d-flex flex-wrap justify-content-center'>
            <button type='submit' className='btn btn-primary me-4'>
              <span className='indicator-label'>Send Reset Code</span>
              {loading && (
                <span className='indicator-progress'>
                  Please wait...
                  <span className='spinner-border spinner-border-sm ms-2'></span>
                </span>
              )}
            </button>

            <Link to='/auth/login' className='btn btn-light'>
              Cancel
            </Link>
          </div>
        </>
      )}

      {/* STEP 2 — VERIFY CODE */}
      {step === 'code' && (
        <>
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>
              Enter verification code
            </label>

            <input
              type='text'
              className='form-control bg-transparent'
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>

          <div className='d-flex justify-content-center'>
            <button
              type='button'
              className='btn btn-primary'
              disabled={fetchStatus === 'fetching'}
              onClick={verifyCode}
            >
              Verify Code
            </button>
          </div>
        </>
      )}

      {/* STEP 3 — NEW PASSWORD */}
      {step === 'password' && (
        <>
          <div className='fv-row mb-8'>
            <label className='form-label fw-bolder text-gray-900 fs-6'>New Password</label>

            <input
              type='password'
              className='form-control bg-transparent'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className='d-flex justify-content-center'>
            <button
              type='button'
              className='btn btn-primary'
              disabled={fetchStatus === 'fetching'}
              onClick={submitNewPassword}
            >
              Reset Password
            </button>
          </div>
        </>
      )}
    </form>
  )
}
